/**
 * The transaction edit/create dialog: loads the transaction being edited,
 * holds the form fields and saves through the create/update mutations. The
 * payload mapping is `toTransactionPayload` (transactionPayload.ts). Opened and
 * closed by `TransactionEditProvider`, which remounts it for each open so the
 * fields start empty.
 */

import {
  getSecurityTypeLabel,
  SECURITY_TYPES,
  TransactionType,
  type Account,
} from '@luminaria/shared';
import React, { useEffect, useMemo, useState } from 'react';
import { useModal } from '@app/contexts/ModalContext';
import { NumberInput } from '@shared/components/forms/NumberInput';
import { SelectInput } from '@shared/components/forms/SelectInput';
import { TextInput } from '@shared/components/forms/TextInput';
import { Modal } from '@shared/components/Modal';
import {
  useCreateTransaction,
  useTransaction,
  useUpdateTransaction,
} from '@shared/hooks/useTransactions';
import { formatAction } from '@shared/utils/format';
import {
  emptyForm,
  toTransactionPayload,
  transactionToForm,
  type TransactionFormMode,
  type TransactionFormState,
} from './transactionPayload';

type TextFieldName =
  | 'symbol'
  | 'description'
  | 'tradeDate'
  | 'settlementDate';

type NumberFieldName =
  | 'quantity'
  | 'price'
  | 'amount'
  | 'fees';

const CASH_FLOW_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'deposit', label: 'Deposit' },
  { value: 'withdrawal', label: 'Withdrawal' },
];

interface TransactionFormProps {
  /** null: closed. */
  mode: TransactionFormMode | null;
  editTarget: { accountId: string; txId: string } | null;
  createDefaultAccountId?: string;
  /** Accounts available for create mode. */
  accounts: Account[];
  onClose: () => void;
}

export function TransactionForm({ mode, editTarget, createDefaultAccountId, accounts, onClose }: TransactionFormProps) {
  const [txForm, setTxForm] = useState<TransactionFormState>(emptyForm);
  const { alert } = useModal();
  const { data: transaction, isLoading } = useTransaction(editTarget?.txId ?? '');

  const updateTransactionMutation = useUpdateTransaction();
  const createTransactionMutation = useCreateTransaction();

  // Populate form from existing transaction when entering edit mode.
  useEffect(() => {
    if (mode !== 'edit' || !transaction || !editTarget) return;
    setTxForm(transactionToForm(transaction, editTarget.accountId));
  }, [mode, transaction, editTarget]);

  // Initialize form when entering create mode.
  useEffect(() => {
    if (mode !== 'create') return;
    setTxForm({
      ...emptyForm,
      accountId: createDefaultAccountId
        ?? accounts[0]?.id
        ?? '',
    });
  }, [mode, createDefaultAccountId, accounts]);

  function handleClose() {
    setTxForm(emptyForm);
    onClose();
  }

  const transactionTypeOptions = useMemo(
    () => Object.values(TransactionType).map((value) => ({
      value,
      label: formatAction(value),
    })),
    []
  );

  const securityTypeOptions = useMemo(
    () => SECURITY_TYPES.map((value) => ({
      value,
      label: getSecurityTypeLabel(value),
    })),
    []
  );

  const accountOptions = useMemo(
    () => accounts.map((a) => ({ value: a.id, label: a.name })),
    [accounts]
  );

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!mode) return;

    const result = toTransactionPayload(txForm, mode, mode === 'edit' ? transaction : undefined);

    if (result.kind === 'invalid') {
      await alert({
        title: 'Invalid transaction',
        message: 'Action, amount, and at least one date are required.',
        type: 'warning',
      });
      return;
    }

    if (result.kind === 'missingAccount') {
      await alert({
        title: 'Account required',
        message: 'Select the account this transaction belongs to.',
        type: 'warning',
      });
      return;
    }

    try {
      if (result.kind === 'update') {
        if (!editTarget) return;
        await updateTransactionMutation.mutateAsync({ id: editTarget.txId, ...result.payload });
      } else if (result.kind === 'create') {
        await createTransactionMutation.mutateAsync(result.payload);
      } else {
        // Edit mode before the transaction loaded: nothing to save yet.
        return;
      }

      handleClose();
    } catch {
      await alert({
        title: mode === 'edit' ? 'Unable to update transaction' : 'Unable to create transaction',
        message: 'The transaction could not be saved. Please review the values and try again.',
        type: 'error',
      });
    }
  }

  const handleTextField = (name: TextFieldName) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setTxForm(f => ({ ...f, [name]: e.target.value }));
  };

  const handleNumberField = (name: NumberFieldName) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setTxForm(f => ({ ...f, [name]: e.target.value }));
  };

  const isOpen = mode !== null;
  const isCreate = mode === 'create';
  const showLoading = mode === 'edit' && isLoading;
  const isPending = updateTransactionMutation.isPending || createTransactionMutation.isPending;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isCreate ? 'Add Transaction' : 'Edit Transaction'}
      maxWidth="540px"
    >
      <form onSubmit={handleSubmit} className="p-4">
        {showLoading && (
          <div className="flex items-center gap-2 text-sm text-text-muted py-4">
            <span className="inline-block w-4 h-4 border-2 border-border border-t-primary rounded-full animate-spin" />
            Loading transaction...
          </div>
        )}
        {!showLoading && (
          <>
            {isCreate && (
              <div className="mb-3">
                <SelectInput
                  label="Account"
                  size="sm"
                  value={txForm.accountId}
                  onChange={e => setTxForm(f => ({ ...f, accountId: e.target.value }))}
                  required
                  options={accountOptions}
                  placeholder={accountOptions.length === 0 ? 'No accounts available' : 'Select account'}
                  disabled={accountOptions.length === 0}
                />
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <SelectInput
                label="Action"
                size="sm"
                value={txForm.action}
                onChange={e => setTxForm(f => ({ ...f, action: e.target.value }))}
                required
                options={transactionTypeOptions}
                placeholder="Select action"
              />
              <SelectInput
                label="Security Type"
                size="sm"
                value={txForm.securityType}
                onChange={e => setTxForm(f => ({ ...f, securityType: e.target.value }))}
                options={[{ value: '', label: 'Auto' }, ...securityTypeOptions]}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              <TextInput
                label="Trade Date"
                type="date"
                size="sm"
                value={txForm.tradeDate}
                onChange={handleTextField('tradeDate')}
              />
              <TextInput
                label="Settlement Date"
                type="date"
                size="sm"
                value={txForm.settlementDate}
                onChange={handleTextField('settlementDate')}
              />
            </div>
          </>
        )}
        <div className="mt-3">
          <TextInput
            label="Symbol"
            size="sm"
            value={txForm.symbol}
            onChange={handleTextField('symbol')}
          />
        </div>
        <div className="mt-3">
          <TextInput
            label="Description"
            size="sm"
            value={txForm.description}
            onChange={handleTextField('description')}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <NumberInput
            label="Quantity"
            size="sm"
            step="any"
            value={txForm.quantity}
            onChange={handleNumberField('quantity')}
          />
          <NumberInput
            label="Price"
            size="sm"
            step="any"
            value={txForm.price}
            onChange={handleNumberField('price')}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <NumberInput
            label="Amount"
            size="sm"
            step="any"
            value={txForm.amount}
            onChange={handleNumberField('amount')}
            required
          />
          <NumberInput
            label="Fees"
            size="sm"
            step="any"
            min="0"
            value={txForm.fees}
            onChange={handleNumberField('fees')}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <SelectInput
            label="Cash Flow Type"
            size="sm"
            value={txForm.cashFlowType}
            onChange={e => setTxForm(f => ({
              ...f,
              cashFlowType: e.target.value as '' | 'deposit' | 'withdrawal',
            }))}
            options={CASH_FLOW_OPTIONS}
          />
          <label className="flex items-center gap-2 mt-7 text-sm text-text cursor-pointer">
            <input
              type="checkbox"
              checked={txForm.excludeFromCostBasis}
              onChange={e => setTxForm(f => ({ ...f, excludeFromCostBasis: e.target.checked }))}
              className="rounded border-border"
            />
            Exclude from cost basis
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button type="button" className="btn-secondary" onClick={handleClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={showLoading || isPending}
          >
            {isCreate ? 'Create Transaction' : 'Save Transaction'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
